import React from 'react';
import { ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

interface SessionTimeoutWarningProps {
  isVisible: boolean;
  /** Seconds remaining before automatic logout. */
  timeRemaining: number;
  /** Which timeout feature triggered the warning. */
  warningSource?: 'absolute' | 'idle' | 'none';
  onExtendSession: () => void;
  onLogoutNow: () => void;
}

function formatCountdown(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (mins > 0) {
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  }
  return `${secs}s`;
}

const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  isVisible,
  timeRemaining,
  warningSource = 'idle',
  onExtendSession,
  onLogoutNow,
}) => {
  if (!isVisible) return null;

  const isAbsolute = warningSource === 'absolute';
  const countdown = formatCountdown(Math.max(0, timeRemaining));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className={`h-8 w-8 ${isAbsolute ? 'text-red-500' : 'text-amber-500'}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isAbsolute ? 'Maximum Session Duration Reached' : 'Session Timeout Warning'}
              </h3>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <ClockIcon className="h-5 w-5 text-gray-800" />
              <span className="text-gray-700">
                You will be logged out in{' '}
                <span className="font-semibold text-red-600">{countdown}</span>
              </span>
            </div>
            <p className="text-gray-900 text-sm">
              {isAbsolute
                ? 'Your maximum login duration has been reached. Please save any work in progress before you are automatically logged out. You may log in again afterwards.'
                : 'For security reasons, you will be automatically logged out due to inactivity. Click "Stay Logged In" to continue working, or "Logout Now" to logout immediately.'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            {!isAbsolute && (
              <button
                onClick={onExtendSession}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Stay Logged In
              </button>
            )}
            <button
              onClick={onLogoutNow}
              className={`flex-1 ${isAbsolute ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-600 hover:bg-gray-700'} text-white font-medium py-2 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2`}
            >
              Logout Now
            </button>
          </div>

          {/* Auto-logout countdown */}
          <div className="mt-3 text-center">
            <span className="text-xs text-gray-800">
              Automatic logout in {countdown}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
